"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.logWhatsAppClick = exports.whatsAppClickStamp = exports.newWhatsAppClickCode = exports.CLICK_CODE_PATTERN = exports.getAttributionPayload = exports.mirrorAttributionCookie = exports.readAttributionState = exports.decodeAttributionValue = exports.ATTRIBUTION_COOKIE = void 0;
var StorageApi_1 = require("@/api/browser/StorageApi");
/**
 * Marketing attribution (#4742).
 *
 * The server writes the first-party `okattr` cookie on every landing that carries utm_* / an ad click id /
 * an external referrer (see BaseController.CaptureAttribution). The browser only mirrors that cookie into
 * localStorage and, at enquiry submit, hands the server whatever it can still see so an expired or cleared
 * cookie does not lose the attribution. All of this is a hint: the server re-derives the channel itself.
 */
exports.ATTRIBUTION_COOKIE = 'okattr';
var ATTRIBUTION_STORAGE_KEY = 'okattr';
var ENCODED_PREFIX = 'b1.';
/**
 * Cookie value → JSON text. The server writes "b1." + base64 (with '.' and '_' for '+' and '/', no padding) so the
 * cookie only holds characters the WAF ignores; cookies set before that are URL-encoded JSON and are still read.
 */
exports.decodeAttributionValue = function (raw) {
    if (raw.indexOf(ENCODED_PREFIX) !== 0) {
        return raw.indexOf('%') >= 0 ? decodeURIComponent(raw) : raw;
    }
    var binary = atob(raw.slice(ENCODED_PREFIX.length).replace(/\./g, '+').replace(/_/g, '/'));
    var escaped = '';
    for (var i = 0; i < binary.length; i++)
        escaped += "%" + ("00" + binary.charCodeAt(i).toString(16)).slice(-2);
    return decodeURIComponent(escaped); // bytes → UTF-8 text
};
var parseState = function (raw) {
    if (!raw)
        return undefined;
    try {
        var state = JSON.parse(exports.decodeAttributionValue(raw));
        return state && (state.ft || state.lt) ? state : undefined;
    }
    catch (_a) {
        return undefined;
    }
};
/** Cookie wins; localStorage is the mirror kept for when the cookie is gone. */
exports.readAttributionState = function () {
    if (typeof window === 'undefined')
        return undefined;
    var fromCookie = parseState(StorageApi_1.readCookie(exports.ATTRIBUTION_COOKIE));
    if (fromCookie)
        return fromCookie;
    try {
        return parseState(StorageApi_1.readLocalStorage(ATTRIBUTION_STORAGE_KEY));
    }
    catch (_a) {
        return undefined;
    }
};
/** Copy the server-set cookie into localStorage. Safe to call on every page load. */
exports.mirrorAttributionCookie = function () {
    if (typeof window === 'undefined')
        return;
    try {
        var raw = StorageApi_1.readCookie(exports.ATTRIBUTION_COOKIE);
        if (raw && parseState(raw)) {
            StorageApi_1.setLocalStorage(ATTRIBUTION_STORAGE_KEY, exports.decodeAttributionValue(raw));
        }
    }
    catch (_a) {
        // storage blocked: nothing to do, the cookie alone still works
    }
};
/** `_ga` = "GA1.1.1234567890.1700000000" → "1234567890.1700000000" */
var gaClientId = function () {
    var ga = StorageApi_1.readCookie('_ga');
    if (!ga)
        return undefined;
    var parts = ga.split('.');
    return parts.length >= 4 ? parts[parts.length - 2] + "." + parts[parts.length - 1] : ga;
};
/** `_ga_<container>` = "GS1.1.1700000000.3.1..." → "1700000000" */
var gaSessionId = function () {
    var m = document.cookie.match(/(?:^|;\s*)_ga_[A-Z0-9]+=([^;]*)/);
    if (!m)
        return undefined;
    var parts = m[1].split('.');
    return parts.length >= 3 ? parts[2] : undefined;
};
exports.getAttributionPayload = function () {
    if (typeof window === 'undefined')
        return undefined;
    try {
        exports.mirrorAttributionCookie();
        return {
            state: exports.readAttributionState(),
            submitPage: window.location.href,
            gaClientId: gaClientId(),
            gaSessionId: gaSessionId(),
            gclAw: StorageApi_1.readCookie('_gcl_aw') || undefined,
            fbp: StorageApi_1.readCookie('_fbp') || undefined,
            fbc: StorageApi_1.readCookie('_fbc') || undefined,
            language: navigator.language,
            screen: window.screen ? window.screen.width + "x" + window.screen.height : undefined,
        };
    }
    catch (_a) {
        return { submitPage: window.location.href };
    }
};
// ---------------------------------------------------------------------------
// WhatsApp click codes (#4742 option 2)
//
// The property-page WhatsApp button opens WhatsApp directly, so the server never sees the click. We stamp a
// short code into the pre-filled message ("... [WK7Q2X]"), open WhatsApp synchronously (no popup-blocker risk),
// and log the click in the background. CS pastes the client's first message into the lead notes, and a nightly
// job matches the code back to this click, giving the lead its campaign.
// ---------------------------------------------------------------------------
var CLICK_CODE_ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789'; // no 0/O/1/I/L
exports.CLICK_CODE_PATTERN = /^W[A-HJ-NP-Z2-9]{5}$/;
exports.newWhatsAppClickCode = function () {
    var code = 'W';
    var cryptoObj = typeof window !== 'undefined' ? (window.crypto || window.msCrypto) : undefined;
    if (cryptoObj && cryptoObj.getRandomValues) {
        var bytes = new Uint8Array(5);
        cryptoObj.getRandomValues(bytes);
        for (var i = 0; i < 5; i++)
            code += CLICK_CODE_ALPHABET[bytes[i] % CLICK_CODE_ALPHABET.length];
    }
    else {
        for (var i = 0; i < 5; i++)
            code += CLICK_CODE_ALPHABET[Math.floor(Math.random() * CLICK_CODE_ALPHABET.length)];
    }
    return code;
};
/** Text stamped onto the WhatsApp message so the code survives into the lead notes. */
exports.whatsAppClickStamp = function (code) { return " [" + code + "]"; };
/** Fire-and-forget: sendBeacon survives the tab navigating to WhatsApp; fetch keepalive is the fallback. */
exports.logWhatsAppClick = function (languageCode, payload) {
    if (typeof window === 'undefined')
        return;
    try {
        var url = "/" + languageCode + "/api/communication/whatsappclick";
        var body = JSON.stringify(__assign(__assign({}, payload), { attribution: payload.attribution || exports.getAttributionPayload() }));
        if (navigator.sendBeacon && navigator.sendBeacon(url, new Blob([body], { type: 'application/json' })))
            return;
        if (typeof fetch === 'function') {
            fetch(url, { method: 'POST', body: body, keepalive: true, credentials: 'include', headers: { 'Content-Type': 'application/json' } }).catch(function () { return undefined; });
        }
    }
    catch (_a) {
        // logging must never block opening WhatsApp
    }
};
